import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Icon from '../../../components/AppIcon';
import Image from '../../../components/AppImage';
import Button from '../../../components/ui/Button';
import { useCourseFavorites } from '../../../hooks/useUserProgress';
import { useAuth } from '../../../contexts/AuthContext';
import { listLessonsByModule, getLessonById } from '../../../repos/contentRepo';
import { canAccessLessonContent, isPrivilegedContentRole } from '@/lib/courseAccess';
import { useCourseAccess } from '../../../hooks/useCourseAccess';

const CourseHero = ({ course, userProgress, courseProgress, progressLoading, modules, tenantId }) => {
  const navigate = useNavigate();
  const { user, session } = useAuth();
  const [isLoading, setIsLoading] = useState(false);
  const [accessDeniedMsg, setAccessDeniedMsg] = useState(null);

  const courseIdString = course?.id ? String(course.id) : undefined;
  const systemUserId = session?.systemUser?.id;
  const tenantRole = session?.userTenant?.role;
  const {
    isFullCourseEnrolled,
    isModuleAccessible,
  } = useCourseAccess(systemUserId, courseIdString);

  const realProgress = courseProgress;

  const { checkIfFavorited, toggleFavorite, loading: favoriteLoading } = useCourseFavorites(user?.id);
  const [isFavorited, setIsFavorited] = useState(false);

  React.useEffect(() => {
    if (user?.id && courseIdString) {
      checkIfFavorited(courseIdString).then(setIsFavorited);
    }
  }, [user?.id, courseIdString, checkIfFavorited]);

  const findFirstAccessibleModule = () => {
    if (!modules?.length) return null;
    if (isPrivilegedContentRole(tenantRole) || isFullCourseEnrolled) {
      return modules[0];
    }
    return modules.find((m) => isModuleAccessible(m.id)) || null;
  };

  const handleStartCourse = async () => {
    setAccessDeniedMsg(null);
    if (!user?.id || !course?.id || !tenantId) {
      console.error('❌ Dados insuficientes para iniciar curso');
      return;
    }

    try {
      setIsLoading(true);

      if (isPrivilegedContentRole(tenantRole)) {
        if (modules && modules.length > 0) {
          const firstModule = modules[0];
          navigate(`/course/${course.slug}/module/${firstModule.slug}`);
        } else {
          navigate(`/course/${course.slug}`);
        }
        return;
      }

      if (!systemUserId) {
        setAccessDeniedMsg('Não foi possível verificar seu cadastro. Atualize a página ou entre novamente.');
        return;
      }

      if (!modules || modules.length === 0) {
        navigate(`/course/${course.slug}`);
        return;
      }

      const targetModule = findFirstAccessibleModule();
      if (!targetModule) {
        setAccessDeniedMsg('Você não tem acesso a este curso. Entre em contato ou adquira o módulo para liberar o conteúdo.');
        return;
      }

      const lessons = await listLessonsByModule(targetModule.id);
      const firstLesson = lessons[0];
      const canEnter = await canAccessLessonContent({
        systemUserId,
        courseId: courseIdString,
        moduleId: String(targetModule.id),
        lessonIsFree: !!firstLesson?.is_free,
        userTenantRole: tenantRole,
      });

      if (!canEnter) {
        setAccessDeniedMsg('Você não tem acesso a este curso. Entre em contato ou adquira o curso para liberar o conteúdo.');
        return;
      }

      navigate(`/course/${course.slug}/module/${targetModule.slug}`);
    } catch (error) {
      console.error('❌ Erro ao iniciar curso:', error);
      setAccessDeniedMsg('Não foi possível iniciar o curso. Tente novamente.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleContinueCourse = async () => {
    setAccessDeniedMsg(null);
    if (!user?.id || !course?.id) {
      console.error('❌ Dados insuficientes para continuar curso');
      return;
    }

    if (isPrivilegedContentRole(tenantRole)) {
      if (realProgress?.nextLesson?.id) {
        const moduleMatch = modules?.find((m) => m.id === realProgress.nextLesson.moduleId);
        if (moduleMatch) {
          navigate(`/course/${course.slug}/module/${moduleMatch.slug}/lesson/${realProgress.nextLesson.id}`);
          return;
        }
      }
      if (modules && modules.length > 0) {
        const firstModule = modules[0];
        navigate(`/course/${course.slug}/module/${firstModule.slug}`);
      }
      return;
    }

    if (!systemUserId) {
      setAccessDeniedMsg('Não foi possível verificar seu cadastro. Atualize a página ou entre novamente.');
      return;
    }

    try {
      setIsLoading(true);

      if (realProgress?.nextLesson?.id) {
        const lessonRow = await getLessonById(String(realProgress.nextLesson.id));
        const ok = await canAccessLessonContent({
          systemUserId,
          courseId: courseIdString,
          moduleId: realProgress.nextLesson.moduleId
            ? String(realProgress.nextLesson.moduleId)
            : undefined,
          lessonIsFree: !!lessonRow?.is_free,
          userTenantRole: tenantRole,
        });
        if (!ok) {
          setAccessDeniedMsg('Você não tem acesso a esta lição. Verifique sua matrícula no curso ou módulo.');
          return;
        }
        const moduleMatch = modules?.find((m) => m.id === realProgress.nextLesson.moduleId);
        if (moduleMatch) {
          navigate(`/course/${course.slug}/module/${moduleMatch.slug}/lesson/${realProgress.nextLesson.id}`);
          return;
        }
      }

      if (modules && modules.length > 0) {
        const targetModule = findFirstAccessibleModule();
        if (!targetModule) {
          setAccessDeniedMsg('Você não tem acesso a este curso.');
          return;
        }
        const lessons = await listLessonsByModule(targetModule.id);
        const firstLesson = lessons[0];
        if (firstLesson?.id) {
          const ok = await canAccessLessonContent({
            systemUserId,
            courseId: courseIdString,
            moduleId: String(targetModule.id),
            lessonIsFree: !!firstLesson.is_free,
            userTenantRole: tenantRole,
          });
          if (!ok) {
            setAccessDeniedMsg('Você não tem acesso a este curso.');
            return;
          }
          navigate(`/course/${course.slug}/module/${targetModule.slug}/lesson/${firstLesson.id}`);
          return;
        }
        navigate(`/course/${course.slug}/module/${targetModule.slug}`);
      }
    } catch (error) {
      console.error('❌ Erro ao continuar curso:', error);
      setAccessDeniedMsg('Não foi possível continuar. Tente novamente.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleSaveForLater = async () => {
    if (!user?.id || !course?.id) {
      console.error('❌ Dados insuficientes para favoritar');
      return;
    }

    try {
      const newFavoritedState = await toggleFavorite(courseIdString);
      setIsFavorited(newFavoritedState);
    } catch (error) {
      console.error('❌ Erro ao alternar favorito:', error);
    }
  };

  const getProgressPercentage = () => {
    if (realProgress) {
      return realProgress.progressPercentage;
    }
    if (userProgress) {
      return Math.round((userProgress?.completedLessons / userProgress?.totalLessons) * 100);
    }
    return 0;
  };

  const isStarted =
    realProgress?.isStarted ||
    (userProgress && userProgress?.completedLessons > 0);

  return (
    <div className="relative bg-gradient-to-r from-gray-500 to-black rounded-lg overflow-hidden mb-8">
      <div className="absolute inset-0 bg-black bg-opacity-40"></div>
      <div className="relative z-10 flex flex-col lg:flex-row items-center p-8 lg:p-12">
        <div className="w-full lg:w-1/3 mb-6 lg:mb-0 lg:mr-8">
          <div className="relative aspect-video rounded-lg overflow-hidden shadow-tier-2">
            <Image
              src={course?.thumbnail}
              alt={course?.title}
              className="w-full h-full object-cover"
            />
            <div className="absolute inset-0 bg-black bg-opacity-20 flex items-center justify-center">
              <div className="w-16 h-16 bg-white bg-opacity-90 rounded-full flex items-center justify-center">
                <Icon name="Play" size={24} className="text-primary ml-1" />
              </div>
            </div>
          </div>
        </div>

        <div className="flex-1 text-center lg:text-left">
          <div className="mb-4">
            <span className="inline-block bg-secondary text-secondary-foreground px-3 py-1 rounded-full text-sm font-medium mb-3">
              {course?.category}
            </span>
            <h1 className="text-3xl lg:text-4xl font-heading font-bold text-white mb-2">
              {course?.title}
            </h1>
            <p className="text-lg text-white opacity-90 mb-4">
              {course?.description}
            </p>
          </div>

          <div className="flex items-center justify-center lg:justify-start mb-6">
            <div className="w-12 h-12 rounded-full overflow-hidden mr-3">
              <Image
                src={course?.instructor?.avatar}
                alt={course?.instructor?.name}
                className="w-full h-full object-cover"
              />
            </div>
            <div className="text-left">
              <p className="text-white font-medium">{course?.instructor?.name}</p>
              <p className="text-white opacity-75 text-sm font-caption">
                {course?.instructor?.title}
              </p>
            </div>
          </div>

          {isStarted && (
            <div className="mb-6">
              <div className="flex items-center justify-between mb-2">
                <span className="text-white text-sm">Progresso do Curso</span>
                <span className="text-white text-sm font-medium">
                  {getProgressPercentage()}%
                </span>
              </div>
              <div className="w-full bg-white bg-opacity-20 rounded-full h-2">
                <div
                  className="bg-secondary h-2 rounded-full transition-all duration-300"
                  style={{ width: `${getProgressPercentage()}%` }}
                ></div>
              </div>
            </div>
          )}

          {accessDeniedMsg && (
            <p className="text-sm text-amber-200 mb-4 max-w-xl" role="alert">
              {accessDeniedMsg}
            </p>
          )}

          <div className="flex flex-col sm:flex-row gap-4 justify-center lg:justify-start">
            <Button
              variant="default"
              size="lg"
              onClick={isStarted ? handleContinueCourse : handleStartCourse}
              iconName={isStarted ? 'Play' : 'PlayCircle'}
              iconPosition="left"
              className="bg-secondary text-secondary-foreground hover:bg-secondary/90"
              disabled={isLoading || progressLoading}
            >
              {isLoading ? 'Carregando...' : isStarted ? 'Continuar Curso' : 'Iniciar Curso'}
            </Button>

            <Button
              variant="outline"
              size="lg"
              iconName={isFavorited ? 'BookmarkCheck' : 'Bookmark'}
              iconPosition="left"
              className="border-white text-white hover:bg-white hover:text-primary"
              onClick={handleSaveForLater}
              disabled={favoriteLoading}
            >
              {favoriteLoading ? 'Carregando...' : isFavorited ? 'Favoritado' : 'Salvar para Depois'}
            </Button>
          </div>

          <div className="flex items-center justify-center lg:justify-start gap-6 mt-6 text-white opacity-90">
            <div className="flex items-center gap-2">
              <Icon name="Clock" size={16} />
              <span className="text-sm font-caption">{course?.duration}</span>
            </div>
            <div className="flex items-center gap-2">
              <Icon name="Users" size={16} />
              <span className="text-sm font-caption">{course?.studentsCount} estudantes</span>
            </div>
            <div className="flex items-center gap-2">
              <Icon name="Star" size={16} />
              <span className="text-sm font-caption">{course?.rating}/5</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CourseHero;
